import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '../../../components/PageHeader/PageHeader';
import MenuContext from '../../../context/MenuContext';
import { getTodaysStockCountSession, registerStockCountAttempt } from '../../../services/StockCountService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const RESOLVED_STATUSES = ['COUNTED', 'PENDING_APPROVAL', 'SKIPPED'];

const STATUS_CONFIG = {
  PENDING: { label: 'Pendiente', color: 'default' },
  NEEDS_RECOUNT: { label: 'Volver a contar', color: 'warning' },
  COUNTED: { label: 'Contado', color: 'success' },
  PENDING_APPROVAL: { label: 'Enviado a revisión', color: 'info' },
  SKIPPED: { label: 'Omitido', color: 'default' },
};

const StockCountPage = () => {
  const { locationId } = useParams();
  const { fetchMenu } = useContext(MenuContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({});
  const [submittingItemId, setSubmittingItemId] = useState(null);

  const loadSession = async () => {
    try {
      setLoading(true);
      const data = await getTodaysStockCountSession(locationId);
      setSession(data);
    } catch (error) {
      console.error('Error loading stock count session', error);
      SnackbarUtils.error('No pudimos cargar el conteo de hoy');
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
  }, [locationId]);

  const handleInputChange = (itemId, value) => {
    setInputs((previous) => ({ ...previous, [itemId]: value }));
  };

  const handleSubmitAttempt = async (item) => {
    const rawValue = inputs[item.id];
    if (rawValue === undefined || rawValue === '') {
      SnackbarUtils.error('Ingresá la cantidad contada');
      return;
    }
    const countedStock = Number(rawValue);
    if (Number.isNaN(countedStock) || countedStock < 0) {
      SnackbarUtils.error('Cantidad inválida');
      return;
    }

    try {
      setSubmittingItemId(item.id);
      const result = await registerStockCountAttempt(item.id, countedStock);
      if (result.needsRecount) {
        SnackbarUtils.error('Este ítem no coincide con el sistema, volvé a contarlo');
      } else if (result.matchedSystemStock) {
        SnackbarUtils.success('Coincide con el sistema');
      } else {
        SnackbarUtils.error('Sigue sin coincidir, se envió a revisión');
      }
      setInputs((previous) => ({ ...previous, [item.id]: '' }));
      await loadSession();
      // El menú se genera con los pendientes reales de cada sucursal. Refrescarlo después de
      // cada intento actualiza el número y hace desaparecer el badge al completar la sesión.
      await fetchMenu();
      // La campanita también muestra el conteo pendiente: se actualiza al momento, igual que el badge del menú.
      window.dispatchEvent(new CustomEvent('notifications-refresh'));
    } catch (error) {
      console.error('Error registering stock count attempt', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No pudimos registrar el conteo');
    } finally {
      setSubmittingItemId(null);
    }
  };

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return (
      <Box p={3}>
        <PageHeader title="Conteo de stock" subtitle="Conteo del día para esta sucursal" />
        <Alert severity="info" sx={{ mt: 3 }}>
          Hoy no corresponde conteo en esta sucursal (o no hay conteo de stock configurado).
        </Alert>
      </Box>
    );
  }

  const resolvedCount = session.items.filter((item) => RESOLVED_STATUSES.includes(item.status)).length;
  const progressPercent = session.targetItemCount > 0 ? (resolvedCount / session.targetItemCount) * 100 : 0;

  return (
    <Box p={3}>
      <PageHeader title="Conteo de stock" subtitle="Conteo del día para esta sucursal" />

      <Paper sx={{ p: 2, mt: 3, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">
            {resolvedCount} de {session.targetItemCount} ítems contados
          </Typography>
          {session.carryOverDebt > 0 && (
            <Chip size="small" color="warning" variant="outlined" label={`${session.carryOverDebt} arrastrados de días anteriores`} />
          )}
        </Stack>
        <LinearProgress variant="determinate" value={Math.min(progressPercent, 100)} sx={{ height: 8, borderRadius: 4 }} />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ítem</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Motivo de selección</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Cantidad contada</TableCell>
              <TableCell align="center">Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {session.items.map((item) => {
              const statusConfig = STATUS_CONFIG[item.status] || { label: item.status, color: 'default' };
              const isResolved = RESOLVED_STATUSES.includes(item.status);
              const isBusy = submittingItemId === item.id;

              return (
                <TableRow key={item.id} hover>
                  <TableCell>{item.referenceName || `#${item.referenceId}`}</TableCell>
                  <TableCell>{item.referenceTypeLabel}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{item.selectionReason}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={statusConfig.label} color={statusConfig.color} />
                  </TableCell>
                  <TableCell align="center">
                    {isResolved ? (
                      item.finalCountedStock
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        value={inputs[item.id] ?? ''}
                        onChange={(e) => handleInputChange(item.id, e.target.value)}
                        sx={{ width: 120 }}
                        disabled={isBusy}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!isResolved && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSubmitAttempt(item)}
                        disabled={isBusy}
                      >
                        {isBusy ? <CircularProgress size={18} color="inherit" /> : (item.attemptCount > 0 ? 'Recontar' : 'Registrar')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StockCountPage;
